// @ts-check
/**
 * What the stand sees right after an in-person sale is saved. For a payment link sale it shows the
 * link, a QR the customer can scan from this screen, a WhatsApp button, and watches the order until
 * Razorpay reports it paid.
 */
import { Check, Copy, Loader2, MessageCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminOrderService from '../services/adminOrderService';
import {
  PAYMENT_METHODS,
  advanceReceiptMessage,
  paymentLinkMessage,
  paymentUrl,
  phoneDial,
  receiptMessage,
  whatsappUrl,
} from '../utils/posOrder';
import { STOREFRONT_URL } from '../utils/storefront';

const POLL_MS = 5000;

const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

const waButton =
  'inline-flex items-center justify-center gap-2 px-4 py-3 text-base rounded-lg bg-green-600 text-white hover:bg-green-700';

/**
 * @param {{
 *   response: {id: string, order_number: string, total: number, amount_paid?: number, balance_due?: number,
 *     payment_method: string, payment_status: string, order_status: string, pay_path: string | null, created: boolean},
 *   form: any,
 *   lines: Array<{variant: any, quantity: number, unitPrice: number}>,
 *   onDone: () => void,
 *   onAnother: () => void,
 * }} props
 */
const PosSaleResult = ({ response, form, lines, onDone, onAnother }) => {
  const toast = useToast();
  const [paymentStatus, setPaymentStatus] = useState(response.payment_status);
  const [orderStatus, setOrderStatus] = useState(response.order_status);
  const [pollFailed, setPollFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const isLink = !!response.pay_path;
  const url = paymentUrl(STOREFRONT_URL, response.pay_path);
  const waiting = paymentStatus === 'awaiting_payment' && orderStatus !== 'cancelled';
  const expired = orderStatus === 'cancelled' && paymentStatus !== 'paid';
  const paid = paymentStatus === 'paid';
  const partiallyPaid = paymentStatus === 'partially_paid';

  // Watch a payment link sale until it is paid or the order is cancelled (window expired).
  useEffect(() => {
    if (!isLink || !waiting) return undefined;
    const timer = setInterval(async () => {
      try {
        const order = await adminOrderService.getOrder(response.id);
        setPaymentStatus(order.payment_status);
        setOrderStatus(order.order_status);
        setPollFailed(false);
      } catch {
        setPollFailed(true);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [isLink, waiting, response.id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select the link and copy it by hand.');
    }
  };

  const methodLabel = isLink
    ? 'Razorpay'
    : PAYMENT_METHODS.find((m) => m.value === response.payment_method)?.label || response.payment_method;

  const linkText = paymentLinkMessage({ name: form.firstName.trim(), total: response.total, url });
  const receiptText = receiptMessage({
    name: form.firstName.trim(),
    orderNumber: response.order_number,
    lines,
    total: response.total,
    methodLabel,
    shipToCustomer: form.shipToCustomer,
    dispatchDate: form.dispatchDate,
  });
  const advanceReceiptText = advanceReceiptMessage({
    name: form.firstName.trim(),
    orderNumber: response.order_number,
    lines,
    amountPaid: response.amount_paid,
    balanceDue: response.balance_due,
    dispatchDate: form.dispatchDate,
  });

  return (
    <>
      <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
        <div>
          <h2 className="text-lg font-semibold">
            {response.created ? 'Sale recorded' : 'This sale was already recorded'}: {response.order_number}
          </h2>
          <p className="text-sm text-muted-foreground">
            {inr(response.total)} &middot; {form.firstName.trim()} {form.lastName.trim()}
          </p>
        </div>

        {paid && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 px-4 py-3 text-green-800 dark:text-green-300">
            <Check size={20} /> Paid {inr(response.total)} by {methodLabel}
          </div>
        )}

        {partiallyPaid && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 px-4 py-3 text-amber-800 dark:text-amber-300">
            <Check size={20} /> Advance {inr(response.amount_paid)} received &middot; Balance{' '}
            {inr(response.balance_due)} due
          </div>
        )}

        {isLink && waiting && (
          <>
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 px-4 py-3 text-amber-800 dark:text-amber-300">
              <Loader2 size={18} className="animate-spin" /> Waiting for the customer to pay
            </div>
            {!url ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                REACT_APP_STOREFRONT_URL is not set, so the link cannot be built. Open this order from the Orders page
                after setting it.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-center">
                  <div className="bg-card p-3 border border-border rounded-lg">
                    <QRCodeSVG value={url} size={200} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground break-all">{url}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 text-base border border-input rounded-lg hover:bg-muted/50"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    href={whatsappUrl(phoneDial(form), form.phone, linkText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={waButton}
                  >
                    <MessageCircle size={16} /> Send link on WhatsApp
                  </a>
                </div>
              </div>
            )}
            {pollFailed && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Cannot check the payment right now. Still retrying every few seconds.
              </p>
            )}
          </>
        )}

        {expired && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 px-4 py-3 text-red-800 dark:text-red-300 text-sm">
            The payment window ended without payment, so the order was cancelled and the stock released. Record the sale
            again if the customer still wants it.
          </div>
        )}

        {paid && isLink && !form.shipToCustomer && (
          <p className="text-sm text-muted-foreground">
            A paid link sale lands as Processing. If the customer is taking the piece today, mark the order Delivered
            from the Orders page.
          </p>
        )}

        {(paid || partiallyPaid) && (
          <a
            href={whatsappUrl(phoneDial(form), form.phone, partiallyPaid ? advanceReceiptText : receiptText)}
            target="_blank"
            rel="noopener noreferrer"
            className={`${waButton} w-full`}
          >
            <MessageCircle size={16} /> Send receipt on WhatsApp
          </a>
        )}
      </div>

      <div className="flex gap-2 px-6 py-4 border-t">
        <button
          onClick={onAnother}
          className="px-4 py-3 text-base bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Record another sale
        </button>
        <button onClick={onDone} className="px-4 py-3 text-base border border-input rounded-lg hover:bg-accent">
          Done
        </button>
      </div>
    </>
  );
};

export default PosSaleResult;
